import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiUsers,FiHeart,FiBook,FiMusic,FiGift,FiGlobe,FiWifi,FiClock}=FiIcons;

const Ministries=()=> {
  const ministries=[
    {
      icon: FiHeart,
      title: 'Marriage University',
      description: 'Building stronger marriages through biblical principles, practical wisdom, and community support.',
      image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      programs: [
        'Marriage Enrichment',
        'Pre-marital Counseling',
        'Family Life Workshops',
        'Couples Retreats'
      ]
    },
    {
      icon: FiClock,
      title: 'Every Hour Prayer Community',
      description: 'A continuous prayer chain covering every hour, bringing our community together in constant intercession.',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758489255481-every_hour.jpg',
      programs: [
        '24/7 Prayer Chain',
        'Prayer Warriors',
        'Intercession Training',
        'Prayer Requests'
      ]
    },
    {
      icon: FiUsers,
      title: 'Saviours Youth Church',
      description: 'Dynamic youth ministry with contemporary worship, relevant teaching, and community engagement.',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758487924988-youth_church_img.jpg',
      programs: [
        'Youth Worship',
        'Leadership Training',
        'Community Service',
        'Youth Conferences'
      ]
    },
    {
      icon: FiUsers,
      title: 'Dunamis 7000 Teens',
      description: 'Empowering teenagers with God\'s power, preparing them to be world-changers for this generation.',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758488124472-dunamis_7000_img.jpg',
      programs: [
        'Teen Empowerment',
        'Power Encounters',
        'Leadership Development',
        'Evangelism Training'
      ]
    },
    {
      icon: FiBook,
      title: 'Men\'s Fellowship',
      description: 'Building strong Christian men through fellowship, accountability, and monthly vigil services.',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758488384521-mens_fellowship.jpg',
      programs: [
        'Men\'s Vigil (Last Friday)',
        'Brotherhood Fellowship',
        'Discipleship',
        'Service Projects'
      ]
    },
    {
      icon: FiHeart,
      title: 'Women\'s Fellowship',
      description: 'Empowering women through Bible study, fellowship, and monthly meetings for spiritual growth.',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758488559389-womens_fellowship.jpg',
      programs: [
        'Women\'s Meeting (3rd Tuesday)',
        'Prayer Circle',
        'Community Outreach',
        'Mentorship'
      ]
    },
    {
      icon: FiMusic,
      title: 'Abide Musical Concert',
      description: 'Annual worship concert featuring local and guest artists in praise and worship celebration.',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      programs: [
        'Annual Concert',
        'Worship Team',
        'Guest Artists',
        'Music Ministry Training'
      ]
    },
    {
      icon: FiWifi,
      title: 'Digital Detox & Structure Network',
      description: 'Helping members balance technology use and build meaningful, structured relationships.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      programs: [
        'Digital Wellness',
        'Structured Networking',
        'Life Balance Workshops',
        'Community Building'
      ]
    }
  ];

  return (
    <section id="ministries" className="py-20 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          whileInView={{opacity: 1,y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Our <span className="block text-blue-600 dark:text-blue-400">Ministries & Programs</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Discover ways to grow in faith, serve others, and build meaningful relationships through our diverse ministry programs designed for all ages and interests.
          </p>
        </motion.div>

        {/* Ministries Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {ministries.map((ministry,index)=> (
            <motion.div
              key={index}
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              transition={{duration: 0.6,delay: index * 0.1}}
              viewport={{once: true}}
              className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={ministry.image} 
                  alt={ministry.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-30 transition-all duration-300"></div>
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 bg-white bg-opacity-90 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={ministry.icon} className="w-6 h-6 text-blue-600 dark:text-blue-500" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{ministry.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{ministry.description}</p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Programs Include:</h4>
                  <ul className="space-y-1">
                    {ministry.programs.map((program,programIndex)=> (
                      <li key={programIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mr-2"></div>
                        {program}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-300">
                  Learn More
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          whileInView={{opacity: 1,y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="mt-16 text-center bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md"
        >
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Get Involved</h3>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
            Ready to take the next step in your faith journey? Join one of our ministries and discover how you can serve God and build lasting relationships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-300">
              Join a Ministry
            </button>
            <button className="border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-400 dark:hover:text-gray-900 font-semibold px-8 py-3 rounded-lg transition-all duration-300">
              Volunteer Opportunities
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Ministries;